import { useEffect } from "react";
import Link from "next/link";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useTranslation } from "next-i18next";
import { trackEvent } from "@lib/track-event";
import { Layout } from "@components/Layout/index";
import { getAllTeams, getIndexPage, getModalSettings, getGlobalConfig } from "@lib/api/calls";
import { Render } from "@components/sections/";
import { TeamCard } from "@components/TeamCard";
import styles from "./careers.module.css";

export default function Index({
  preview,
  title,
  seo,
  postingsCount = 0,
  page = [],
  teams = [],
  config,
  subscribed,
  modal,
}) {
  const { t } = useTranslation("careers");
  useEffect(() => {
    trackEvent("view_page", {
      pageTitle: title,
      pageCategory: "Information Page",
      pageLocation: window.location.href,
      pagePath: window.location.pathname,
    });
  }, []);

  return (
    <Layout
      config={config}
      title={title}
      seo={seo}
      subscribed={subscribed}
      isPreview={preview}
      modal={modal}
      pageFound={true}
    >
      <div className={styles.container}>
        <header className={styles.header}>
          <h1>{title}</h1>
        </header>
        {page && <Render sections={page} />}
        {postingsCount > 0 && <hr className={styles.divider} />}
        {postingsCount > 0 && (
          <section className={styles.wrapper}>
            <h2>{t("open-positions")}</h2>
            <div className={styles.postings}>
              {teams.map((team) => {
                if (team.postings.length > 0) {
                  return <TeamCard team={team} key={team.key} />;
                }
              })}
            </div>
          </section>
        )}
        <section className={styles.wrapper}>
          <h2>General Applications</h2>
          <div className={styles.postings}>
            <Link href="/careers/general-applications">
              <a className={styles.general}>
                Don’t see an open role? Send us a general application, we are always looking for
                passionate, curious, and innovative individuals to join our team.
                <span>&rarr;</span>
              </a>
            </Link>
            <Link href="/careers/general-applications-internships">
              <a className={styles.general}>
                Thinking of an internship with us? Apply here.
                <span>&rarr;</span>
              </a>
            </Link>
          </div>
        </section>
        <div className={styles.disclaimer}>
          <small>{t("disclaimer")}</small>
        </div>
      </div>
    </Layout>
  );
}

export async function getStaticProps({ preview = false, locale }) {
  const page = await getIndexPage(preview, locale, "career-index");
  const teams = await getAllTeams(preview);
  const modal = await getModalSettings(locale, preview);
  const config = await getGlobalConfig(locale, preview);

  const seo = {
    seoTitle: page?.meta?.seoTitle || `${page?.title} | Kotn`,
    seoDescription:
      page?.meta?.seoDescription ||
      "We're looking for the best of the curious, talented, and passionate to come join us.",
    openGraphImage: page?.meta?.openGraphImage || null,
    twitterCard: page?.meta?.twitterCard || "summary_large_image",
  };

  return {
    props: {
      preview,
      title: page?.title || null,
      postingsCount: page?.postingsCount || null,
      seo,
      page: page?.blocks || null,
      teams: teams || null,
      modal,
      config,
      ...(await serverSideTranslations(locale, ["common", "navbar", "footer", "careers", "cart"])),
    },
    revalidate: 1800,
  };
}
